import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, Phone, Home as HomeIcon, AlertCircle } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useHumanCheck } from "@/hooks/useHumanCheck";
import PagePlacements from "@/components/PagePlacements";

export default function Contact() {
  const { brand, pages } = useContent();
  const p = pages?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [rodoAccepted, setRodoAccepted] = useState(false);
  const [rodoError, setRodoError] = useState(false);
  const human = useHumanCheck();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Wypełnij imię, email i wiadomość");
      return;
    }
    if (!rodoAccepted) {
      setRodoError(true);
      toast.error("Musisz zaakceptować klauzulę RODO");
      return;
    }
    setRodoError(false);
    if (!human.isValid()) {
      toast.error("Weryfikacja anty-bot nie powiodła się");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, consent_rodo: rodoAccepted, ...human.values });
      toast.success("Dziękujemy! Wkrótce się z Tobą skontaktujemy.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setRodoAccepted(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się wysłać wiadomości.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative max-w-7xl mx-auto px-6 py-20">
      <div className="max-w-2xl">
        <div className="text-white/70 text-xs uppercase tracking-widest font-semibold">{p.eyebrow || "Kontakt"}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-3">
          {p.title || "Porozmawiajmy"}
        </h1>
        <p className="text-white/70 mt-4">
          {p.subtitle || "Masz pytania o ofertę Rmobile? Wypełnij formularz, oddzwonimy w ciągu 24 godzin."}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        <div className="lg:col-span-2 rm-card p-8">
          <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Imię i nazwisko *</label>
              <input data-testid="contact-name" value={form.name} onChange={update("name")} className="rm-input mt-1" placeholder="Jan Kowalski" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Email *</label>
              <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className="rm-input mt-1" placeholder="jan@przyklad.pl" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Telefon</label>
              <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="rm-input mt-1" placeholder="+48 ..." />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Temat</label>
              <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="rm-input mt-1" placeholder="Pytanie o ofertę" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Wiadomość *</label>
              <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} className="rm-input mt-1" placeholder="Twoja wiadomość..." />
            </div>
            {human.element}
            <div
              data-testid="contact-rodo-wrap"
              className={`sm:col-span-2 rounded-lg p-3 border ${rodoError ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"}`}
            >
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  data-testid="contact-rodo"
                  type="checkbox"
                  checked={rodoAccepted}
                  onChange={(e) => { setRodoAccepted(e.target.checked); if (e.target.checked) setRodoError(false); }}
                  className="mt-0.5 accent-[#1f3a8a]"
                />
                <span>
                  Zapoznałem/am się z{" "}
                  <Link to="/rodo" target="_blank" rel="noopener noreferrer" className="text-[#1f3a8a] underline font-semibold">
                    klauzulą informacyjną RODO
                  </Link>
                  . *
                </span>
              </label>
              {rodoError && (
                <div className="rm-error-text mt-2" data-testid="contact-rodo-error">
                  <AlertCircle size={12} /> Zaznaczenie jest obowiązkowe.
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <button data-testid="contact-submit" disabled={loading} type="submit" className="rm-btn-primary disabled:opacity-60">
                {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
              </button>
            </div>
          </form>
        </div>

        <div className="space-y-4">
          {[
            { icon: Phone, label: "Infolinia", value: brand.contact.phone },
            { icon: Mail, label: "Email", value: brand.contact.email },
          ].map((c, i) => (
            <div key={i} className="rm-card-dark p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dc2626]/20 text-[#ef4444] flex items-center justify-center flex-shrink-0">
                <c.icon size={18} />
              </div>
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wider">{c.label}</div>
                <div className="text-white font-semibold mt-1">{c.value}</div>
              </div>
            </div>
          ))}
          {brand.contact.address && (
            <div className="rm-card-dark p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dc2626]/20 text-[#ef4444] flex items-center justify-center flex-shrink-0">
                <HomeIcon size={18} />
              </div>
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Siedziba</div>
                <div className="text-white font-semibold mt-1 whitespace-pre-line">{brand.contact.address}</div>
              </div>
            </div>
          )}
          {brand.contact.hours && (
            <div className="rm-card-dark p-5">
              <div className="text-xs text-white/60 uppercase tracking-wider">Godziny pracy infolinii</div>
              <div className="text-white font-semibold mt-1 whitespace-pre-line">{brand.contact.hours}</div>
            </div>
          )}
        </div>
      </div>
      <PagePlacements page="kontakt" />
    </div>
  );
}
